import { observable, computed, makeObservable, action, runInAction } from 'mobx'
import { move } from '@dnd-kit/helpers'
import _ from 'lodash'
import { debounce } from 'lodash'
import { selectionTypes, ADDITIONAL_INFO_DEFAULT_INPUT } from 'constants.js'
import api from 'api.js'
import Position from './common/Position'
import { catchError } from './ErrorStore'
import { selectionStore, dataStore } from './UIStore'
import { getDefaultGrayColor } from '../utils'
import i18next from '../i18n'

// A marker which a user is placing on the map with a latitude, longitude, and name.
// (After being saved to a target group, markers are represented by SavedMarker)
class Marker {
  @observable id
  @observable created
  @observable name = ''
  @observable description = ''
  @observable color_id
  @observable initial = true
  @observable invalid = observable.map()
  @observable position = new Position()
  @observable additional_info = []
  @observable notes = ''
  @observable contact_detail = { given_name: '', surname: '', email: '', phone: '', address: '', country: '' }
  @observable status
  @observable tag_ids = []

  constructor () {
    makeObservable(this)
  }

  mapMousemove (e) {
    if (this.initial) {
      this.position.latLng = e.latlng
    }
  }

  // Until the user clicks, `initial` is true and
  // the marker follows the cursor. After clicking, the user can
  // drag the marker, but otherwise can move their mouse without
  // the marker following the cursor
  mapMouseup (e) {
    this.initial = false
  }

  get selection () {
    return {
      item: this,
      type: selectionTypes.MARKER,
      data: { focusedInteraction: true }
    }
  }

  getAdditionalInfoItemValue = (id) => {
    const item = this.additional_info.find(item => item.id === id)
    if (item) {
      return item.input
    }
    return null
  }

  @action deleteAdditionalInfoItem = (id) => {
    this.additional_info = this.additional_info.filter(item => item.id !== id)
    this.saveAdditionalInfo()
  }

  @action updateAdditionalInfoItem = (id, field, value) => {
    const index = this.additional_info.findIndex(item => item.id === id)
    if (index === -1) return

    const item = this.additional_info[index]
    this.additional_info[index] = {
      ...item,
      input: {
        ...item.input,
        [field]: value
      }
    }

    this.debouncedSaveAdditionalInfo()
  }

  @action updateAdditionalInfoItemType = (id, type, label) => {
    const index = this.additional_info.findIndex(item => item.id === id)
    if (index === -1) return

    const item = this.additional_info[index]
    if (item.type === type) return

    this.additional_info[index] = {
      ...item,
      type,
      label,
      input: {
        ...ADDITIONAL_INFO_DEFAULT_INPUT[type],
        title: item.input ? item.input.title : null
      }
    }

    this.debouncedSaveAdditionalInfo()
  }

  @action updateNotes = (value) => {
    this.notes = value
    this.debouncedSaveAdditionalInfo()
  }

  debouncedSaveAdditionalInfo = debounce(() => {
    this.saveAdditionalInfo()
  }, 500)

  @action updateContactDetail = (field, value) => {
    this.contact_detail = { ...this.contact_detail, [field]: value }
    this.debouncedSaveContactDetail()
  }

  debouncedSaveContactDetail = debounce(() => {
    this.saveContactDetail()
  }, 800)

  @catchError async addAdditionalInfoItem (item) {
    runInAction(() => { this.additional_info.push(item) })
    await this.saveAdditionalInfo()
  }

  @catchError async saveAdditionalInfo () {
    await api.postMarkerAdditionalInfo(this.id, this.additional_info, this.notes)
  }

  @catchError async saveContactDetail () {
    await api.postMarkerContactDetail(this.id, this.contact_detail)
  }

  @action sortAdditionalInfoItem (event) {
    const sorted_items = move(this.additional_info, event)
    this.additional_info = sorted_items
  }

  @catchError async updateStatus (shapeStatus) {
    const response = await api.patchMarkerStatus(this.id, { shape_status: shapeStatus.id })
    runInAction(() => {
      this.status = response.data.shape_status
    })
  }

  @catchError
  async updateTags (tagId) {
    runInAction(() => {
      if (!this.tag_ids.includes(tagId)) this.tag_ids.push(tagId)
    })
    await api.postMarkerTags(this.id, { tag_ids: [tagId] })
  }

  @catchError
  async deleteTag (tagId) {
    runInAction(() => {
      this.tag_ids = this.tag_ids.filter(id => tagId !== id)
    })
    await api.deleteMarkerTag(this.id, tagId)
  }

  @computed get getColor () {
    return _.find(dataStore.markerColors, item => {
      return item.id === this.colorId
    })
  }

  @computed get colorId () {
    return this.color_id ? this.color_id : this.defaultMarkerColorId
  }

  @computed get defaultMarkerColorId () {
    const defaultColor = getDefaultGrayColor(dataStore && dataStore.markerColors)
    return defaultColor ? defaultColor.id : null
  }

  validate (targetGroup) {
    const group = targetGroup || (selectionStore.data && selectionStore.data.group) || (dataStore.targetGroups && dataStore.targetGroups[0])
    const savedMarkers = group && group.savedMarkers ? group.savedMarkers : []

    const savedMarker = _.find(savedMarkers, {
      id: this.id
    })
    if (
      (savedMarker && savedMarker.name !== this.name && _.find(savedMarkers, { name: this.name })) ||
      (!this.id && this.name && _.find(savedMarkers, { name: this.name }))
    ) {
      this.invalid.set('name', i18next.t('GLOBAL.DUPLICATE_ELEMENT_NAME', { element: i18next.t('GLOBAL.MARKER') }))
      return false
    }

    this.invalid.clear()
    if (this.name === null || _.isEmpty(this.name.toString())) {
      this.invalid.set('name', i18next.t('INVALID.REQUIRED'))
    }
    if (this.position.longitude === null || _.isEmpty(this.position.longitude.toString())) {
      this.invalid.set('longitude', i18next.t('INVALID.REQUIRED'))
    }
    if (this.position.latitude === null || _.isEmpty(this.position.latitude.toString())) {
      this.invalid.set('latitude', i18next.t('INVALID.REQUIRED'))
    }

    return this.invalid.size === 0
  }

  @catchError async edit (targetGroupId) {
    if (this.validate()) {
      const result = await api.patchMarker(this.id, {
        name: this.name,
        description: this.description,
        latitude: this.position.latitude,
        longitude: this.position.longitude,
        target_group: targetGroupId,
        color: this.colorId
      })
      return result.data
    } else {
      return null
    }
  }

  @catchError async add (targetGroupId, addToGeneral = true) {
    if (this.validate()) {
      const result = await api.postMarker({
        name: this.name,
        description: this.description,
        latitude: this.position.latitude,
        longitude: this.position.longitude,
        target_group: targetGroupId,
        color: this.colorId,
        is_general: addToGeneral,
        is_competitor: false,
        is_ally: false
      }
      )
      return result.data
    } else {
      return null
    }
  }

  /** Add marker to draft mode instead of saving to backend immediately */
  addToDraft (poi) {
    if (!this.validate()) {
      return false
    }

    if (!this.position.latLng) {
      this.invalid.set('name', i18next.t('ITEM.PLACE_MARKER_REQUIRED') || 'Please place a marker first')
      return false
    }

    // Store the marker data in the POI draft state
    poi.setDraftMarker({
      name: this.name,
      position: {
        lat: this.position.latitude,
        lng: this.position.longitude
      },
      description: this.description,
      color_id: this.colorId
    })

    return true
  }
}
export default Marker
